import { Field as ReactField } from 'react-final-form';
import { getLanguage, t, translate } from 'react-switch-lang';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from './DatePicker';
import { validateDateOfBirth } from '../utils/Validation';
import { formatDateOfBirth } from '../utils/Format';
import 'react-datepicker/dist/react-datepicker.css';
import MapleButton from './MapleButton';
import { setScreenReaderAlert } from '../utils/Accessibility';
import { events } from '../utils/Amplitude';

export function parseStringToDate(dateStr) {
  if (validateDateOfBirth(dateStr)) return null;

  const d = new Date();
  const ar = dateStr.split('-');
  d.setFullYear(parseInt(ar[0], 10));
  d.setMonth(parseInt(ar[1], 10) - 1);
  d.setDate(parseInt(ar[2], 10));
  return d;
}

export function parseDateToString(date) {
  if (!(date instanceof Date)) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const year = date.getFullYear();

  return [year, month, day].join('-');
}

export function Label({ text, id, name, optional, formatText }) {
  return (
    <label htmlFor={id} id={`${name}Lbl`}>
      {text}
      {optional && <span className="optionalLbl">{`(${t('Form_Lbl_Optional')})`}</span>}
      {formatText && <p className="formatLbl">{formatText}</p>}
    </label>
  );
}

export const Default = ({
  id,
  name,
  label,
  placeholder,
  helpText,
  formatText,
  initialValue = '',
  format = undefined,
  parse = format,
  autoComplete = '',
  className = '',
  optional = false,
  disabled = false,
}) => (
  <ReactField
    name={name}
    initialValue={initialValue}
    formatOnBlur={!!format}
    format={format}
    parse={parse}
  >
    {({ input, meta }) => (
      <div className={`${className} formField`}>
        <Label text={label} name={name} optional={optional} id={id} formatText={formatText} />

        <div>
          <input
            {...input}
            id={id}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete={autoComplete}
            className={`form-control ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : 'is-valid')}`}
          />
        </div>

        {(meta.error || meta.submitError) && meta.touched && (
          <span id={`${id}Error`} className="errorTxt invalid-feedback">{meta.error || meta.submitError}</span>
        )}

        {helpText && <p className="helpTxt">{helpText}</p>}
      </div>
    )}
  </ReactField>
);

export const MailCheckEmail = ({
  id,
  name,
  label,
  helpText,
  formatText,
  initialValue = '',
  format = undefined,
  parse = format,
  className = '',
  optional = false,
  disabled = false,
  invalidEmailSuggestion,
  setInvalidEmailSuggestion,
  change,
  setShowEmailConfirm,
  showEmailConfirm,
}) => (
  <ReactField
    name={name}
    initialValue={initialValue}
    formatOnBlur={!!format}
    format={format}
    parse={parse}
  >
    {({ input, meta }) => (
      <div className={`${className} formField`}>
        <Label text={label} name={name} optional={optional} id={id} formatText={formatText} />

        <div>
          <input
            {...input}
            id={id}
            name={name}
            disabled={disabled}
            placeholder={t('Auth_Field_Email_Placeholder')}
            autoComplete="email"
            className={`form-control ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : 'is-valid')}`}
          />
        </div>

        {(meta.error || meta.submitError) && meta.touched && (
          <>
            <span id={`${id}Error`} className="errorTxt invalid-feedback">{meta.error || meta.submitError}</span>
            {invalidEmailSuggestion && !showEmailConfirm && (
              <div className="invalidEmailContainer">
                <span>{t('Registration_MailCheck_DidYouMean').replace('xEmail', invalidEmailSuggestion)}</span>
                <div>
                  <MapleButton
                    text={t('Button_Yes')}
                    className="linkBtn"
                    ampEvent={events.USER_CLICKED_MAILCHECK_SUGGESTION_YES_BUTTON}
                    ariaLabel={t('ButtonDescription_Registration_MailCheck_Yes').replace('xEmail', invalidEmailSuggestion)}
                    onClickEvent={() => {
                      setInvalidEmailSuggestion(null);
                      change('email', invalidEmailSuggestion);
                    }}
                  />
                  <MapleButton
                    text={t('Button_No')}
                    className="linkBtn"
                    ampEvent={events.USER_CLICKED_MAILCHECK_SUGGESTION_NO_BUTTON}
                    ariaLabel={t('ButtonDescription_Registration_MailCheck_No')}
                    onClickEvent={() => {
                      setInvalidEmailSuggestion(null);
                      setShowEmailConfirm(true);
                      setScreenReaderAlert(t('ScreenReaderAlert_Registraction_ReEnterEmail'));
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {helpText && <p className="helpTxt">{helpText}</p>}
      </div>
    )}
  </ReactField>
);

export function DateField({
  id,
  name,
  label,
  placeholder = t('LinkCard_Field_DoB_Placeholder'),
  helpText,
  formatText = t('LinkCard_Field_DoB_Format'),
  initialValue = '',
  maxDate,
  format = formatDateOfBirth,
  parse = format,
  className = '',
  optional = false,
  setValue,
}) {
  const [isOpen, setOpen] = useState(false);
  const [stateOnMouseDown, setStateOnMouseDown] = useState(false);
  const [selectedDate, setSelectedDate] = useState(parseStringToDate(initialValue) || new Date());

  useEffect(() => {
    if (isOpen) {
      const el = document.querySelector('.react-datepicker');
      el.setAttribute('tabIndex', -1);
      el.focus();
    }
  }, [isOpen]);

  return (
    <ReactField
      name={name}
      initialValue={initialValue}
      formatOnBlur={!!format}
      format={format}
      parse={parse}
    >
      {({ input, meta }) => (
        <div className={`${className} formField`}>
          <Label text={label} name={name} optional={optional} id={id} formatText={formatText} />
          <div className="input-group datepickerContainer">
            <input
              {...input}
              onChange={(e) => {
                const d = parseStringToDate(e.target.value);
                if (d) setSelectedDate(d);
                input.onChange(e);
              }}
              id={id}
              name={name}
              placeholder={placeholder}
              className={`form-control ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : 'is-valid')}`}
            />
            <DatePicker
              onChange={(d) => {
                setSelectedDate(d);
                setValue(name, parseDateToString(d));
              }}
              autoComplete="off"
              dateFormat="yyyy-MM-dd"
              showMonthDropdown
              showYearDropdown
              preventOpenOnFocus
              autoFocus={false}
              autoCorrect={false}
              dropdownMode="select"
              placeholderText={placeholder}
              selected={selectedDate}
              open={isOpen}
              onClickOutside={() => setOpen(false)}
              onSelect={() => setOpen(false)}
              popperClassName="datepickerContainer"
              className="form-control"
              tabIndex="-1"
              disabled
              locale={getLanguage()}
              maxDate={maxDate}
            />
            <button
              className="datepickerBtn"
              type="button"
              title={t('ButtonDescription_Datepicker')}
              // needed to cancel out the effects of onClickOutside
              onMouseDown={() => setStateOnMouseDown(isOpen)}
              onClick={() => {
                if (stateOnMouseDown) {
                  // if popup was open on mouse down, explicitly set isOpen to false
                  // (toggle won't work because OnClickOutside is triggered first)
                  setOpen(false);
                  setStateOnMouseDown(false);
                } else {
                  setOpen(!isOpen);
                }
              }}
            >
              <FontAwesomeIcon icon={['fal', 'calendar-alt']} />
            </button>
          </div>

          {(meta.error || meta.submitError) && meta.touched && (
          <span id={`${id}Error`} className="errorTxt invalid-feedback">{meta.error || meta.submitError}</span>
          )}

          {helpText && <p className="helpTxt">{helpText}</p>}
        </div>
      )}
    </ReactField>
  );
}

export const Amount = ({
  id,
  name,
  helpText,
  initialValue = '',
  format = undefined,
  parse = format,
  className = '',
  placeholder = '$0.00',
}) => (
  <ReactField
    name={name}
    initialValue={initialValue}
    formatOnBlur={!!format}
    format={format}
    parse={parse}
  >
    {({ input, meta }) => (
      <div className={`${className} formField amountField my-auto`}>
        <input
          {...input}
          id={id}
          name={name}
          placeholder={placeholder}
          className="form-control"
        />

        {(meta.error || meta.submitError) && meta.touched && (
          <span id={`${id}Error`} className="errorTxt invalid-feedback">{meta.error || meta.submitError}</span>
        )}

        {helpText && <p className="helpTxt">{helpText}</p>}
      </div>
    )}
  </ReactField>
);

export const Checkbox = ({
  id,
  name,
  label,
  initialValue,
}) => (
  <ReactField name={name} initialValue={initialValue} type="checkbox">
    {({ input, meta }) => (
      <div className="formCheckbox form-check">
        <input
          {...input}
          className={`form-check-input ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : '')}`}
          type="checkbox"
          id={id}
          name={name}
        />
        <label htmlFor={id} className="form-check-label">
          {label}
        </label>
      </div>
    )}
  </ReactField>
);

export function Radio({
  id,
  name,
  label,
  className = '',
  initialValue,
  inline = false,
  value,
}) {
  return (
    <ReactField name={name} initialValue={initialValue} value={value} type="radio">
      {({ input, meta }) => (
        <div className={`${className} form-check ${inline ? 'form-check-inline' : ''} ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : '')}`}>
          <input
            {...input}
            className={`form-check-input ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : '')}`}
            type="radio"
            id={id}
            name={name}
          />
          <label htmlFor={id} className="form-check-label">{label}</label>
        </div>
      )}
    </ReactField>
  );
}

function renderDpdOption() {
  const lang = getLanguage() === 'fr' ? 'French' : 'English';
  return ({ [lang]: text, Peoples_Code: occupationCode, Code: provinceCode }, i) => (
    <option value={occupationCode || provinceCode} key={i}>{text}</option>
  );
}
const DropdownField = ({
  id,
  name,
  label,
  placeholder,
  helpText,
  initialValue,
  autoComplete = undefined,
  className = '',
  values,
  optional = false,
}) => (
  <ReactField name={name} initialValue={initialValue}>
    {({ input, meta }) => (
      <div className={`${className} formField`}>
        <Label text={label} name={name} optional={optional} id={id} />

        <select
          {...input}
          id={id}
          name={name}
          placeholder={placeholder}
          className={`form-select ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : 'is-valid')}`}
          autoComplete={autoComplete}
        >
          <option value="">{t('Form_Dpd_Select')}</option>
          {values.map(renderDpdOption())}
        </select>

        {(meta.error || meta.submitError) && meta.touched && (
          <span id={`${id}Error`} className="errorTxt invalid-feedback">{meta.error || meta.submitError}</span>
        )}

        {helpText && <p className="helpTxt">{helpText}</p>}
      </div>
    )}
  </ReactField>
);
export const Dropdown = translate(DropdownField);

function PasswordField({
  id,
  name,
  label,
  placeholder,
  formatText,
  showRules = false,
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <ReactField name={name}>
      {({ input, meta }) => (
        <div className="formField password">
          <div className="passwordLblDiv">
            <Label text={label} id={id} name={name} />
          </div>
          {formatText && <p className="formatLbl">{formatText}</p>}

          <div className="password-field-container">
            <input
              {...input}
              type={showPassword ? 'text' : 'password'}
              id={id}
              name={name}
              placeholder={placeholder}
              className={`form-control ${(meta.touched || '') && (meta.error || meta.submitError ? 'is-invalid' : 'is-valid')}`}
              autoComplete={showRules ? 'new-password' : 'current-password'}
            />
            <button
              type="button"
              title={
              showPassword ? t('LinkDescription_HidePassword') : t('LinkDescription_ShowPassword')
            }
              className="passwordShowToggle invisibleBtn"
              onClick={() => {
                setShowPassword(!showPassword);
              }}
            >
              <FontAwesomeIcon icon={['fal', 'eye']} className={showPassword ? 'd-none' : ''} />
              <FontAwesomeIcon
                icon={['fal', 'eye-slash']}
                className={!showPassword ? 'd-none' : ''}
              />
            </button>
          </div>

          {(meta.error || meta.submitError) && meta.touched && (
            <span id="passwordError" className="errorTxt invalid-feedback">
              {meta.error || meta.submitError}
            </span>
          )}

          {showRules && (
            <>
              <p className="helpTxt">{t('Auth_Field_Password_Help')}</p>
              <ul className="helpTxt">
                <li>{t('Auth_Field_Password_Help_Length')}</li>
                <li>{t('Auth_Field_Password_Help_SpecialChar')}</li>
                <li>{t('Auth_Field_Password_Help_UppercaseChar')}</li>
                <li>{t('Auth_Field_Password_Help_Number')}</li>
              </ul>
            </>
          )}
        </div>
      )}
    </ReactField>
  );
}
export const Password = translate(PasswordField);
